import { range } from "../../../core/util/FunctionalUtils";
import { MusicalNote } from "../MusicalNote";
import { InstrumentParameter, ParameterMetadata } from "./InstrumentParameter";

/**
 * Base class for pure instrument synthesis without entity or composer dependencies.
 *
 * Provides a clean interface for audio synthesis with parameter management.
 * InstrumentPlayer handles the entity lifecycle and composer integration.
 *
 * Architecture:
 * - Pure synthesis interface - no entity dependencies
 * - Parameter management for real-time control
 * - Audio node management (gain, pan, mute)
 * - Abstract playNote() method for instrument-specific synthesis
 */
export abstract class Instrument {
  protected audioContext: AudioContext;
  protected output: GainNode;
  protected gain: GainNode;
  protected pan: StereoPannerNode;
  protected mute: GainNode;

  // Parameter management
  private parameters: InstrumentParameter[] = [];

  constructor(audioContext: AudioContext, output: GainNode) {
    this.audioContext = audioContext;
    this.output = output;

    // Create audio chain: gain -> instrument gain -> pan -> mute gain -> output
    this.gain = audioContext.createGain();
    this.pan = audioContext.createStereoPanner();
    this.mute = audioContext.createGain();

    this.gain.connect(this.pan);
    this.pan.connect(this.mute);
    this.mute.connect(output);

    // Initialize common parameters
    // Instrument gain parameter - controls audio node directly
    this.addParameter(
      () => this.gain.gain.value,
      (value) => {
        this.gain.gain.value = value;
      },
      {
        key: "gain",
        label: "Gain",
        min: 0.0,
        max: 2.0,
        defaultValue: 1.0,
        step: 0.01,
        category: "Base",
      },
    );

    // Pan parameter - controls audio node directly
    this.addParameter(
      () => this.pan.pan.value,
      (value) => {
        this.pan.pan.value = value;
      },
      {
        key: "pan",
        label: "Pan",
        min: -1.0,
        max: 1.0,
        defaultValue: 0.0,
        step: 0.01,
        category: "Base",
      },
    );
  }

  get monitor(): AudioNode {
    return this.mute; // Return the final output node for monitoring
  }

  /**
   * Abstract method for instrument-specific note synthesis.
   * Each instrument implements this to create its unique sound.
   */
  abstract playNote(
    note: MusicalNote,
    startTime: number,
    duration: number,
  ): void;

  /**
   * Set instrument volume (0-1).
   */
  setVolume(volume: number) {
    this.gain.gain.value = volume;
  }

  getVolume(): number {
    return this.gain.gain.value;
  }

  /**
   * Mute or unmute the instrument.
   */
  setMuted(muted: boolean) {
    this.mute.gain.value = muted ? 0 : 1;
  }

  /**
   * Check if the instrument is muted.
   */
  isMuted(): boolean {
    return this.mute.gain.value === 0;
  }

  /**
   * Toggle mute state.
   */
  toggleMute(): void {
    this.setMuted(!this.isMuted());
  }

  /**
   * Get all valid MIDI note numbers that this instrument can play.
   * This is used for visualization to efficiently allocate space only for playable notes.
   *
   * For discrete instruments (like drums), return specific note numbers.
   * For continuous instruments (like synths), return a range of notes.
   */
  getValidNotes(): number[] {
    // Default implementation: generate continuous range for tonal instruments
    return range(20, 108); // Default MIDI range
  }

  /**
   * Get a display name for this instrument.
   * Used in the music test page for labeling tracks.
   */
  getDisplayName(): string {
    // Use constructor name and remove "Instrument" suffix
    return this.constructor.name.replace("Instrument", "");
  }

  /**
   * Add a parameter to this instrument with getter/setter functions.
   */
  protected addParameter(
    getter: () => number,
    setter: (value: number) => void,
    metadata: Partial<ParameterMetadata> & { key: string; label: string },
  ): void {
    // Reject duplicate parameter keys
    if (this.getParameter(metadata.key)) {
      throw new Error(`Parameter with key "${metadata.key}" already exists`);
    }

    const parameter = new InstrumentParameter(getter, setter, metadata);
    this.parameters.push(parameter);
    parameter.setValue(parameter.defaultValue);
  }

  /**
   * Get all parameters for this instrument.
   */
  getParameters(): InstrumentParameter[] {
    return [...this.parameters];
  }

  /**
   * Find a parameter by key.
   */
  getParameter(key: string): InstrumentParameter | undefined {
    return this.parameters.find((p) => p.key === key);
  }

  /**
   * Set a parameter value by key.
   */
  setParameter(key: string, value: number): void {
    const param = this.getParameter(key);
    if (param) {
      param.setValue(value);
    } else {
      console.warn(`Unknown parameter: ${key}`);
    }
  }

  /**
   * Reset all parameters to their default values.
   */
  resetParametersToDefaults(): void {
    for (const param of this.parameters) {
      param.setValue(param.defaultValue);
    }
  }

  /**
   * Cleanup audio nodes when instrument is no longer needed.
   */
  onDestroy(): void {
    // Disconnect audio nodes to prevent memory leaks
    this.gain.disconnect();
    this.pan.disconnect();
    this.mute.disconnect();
  }
}
